/**
 * MH-33 Danh sách đài truyền thanh (theo mẫu): dòng tỉnh thu gọn được, mỗi xã/phường
 * một dòng với đài ứng dụng CNTT, tổng thiết bị, đang/mất kết nối (hiện tại) và tổng
 * bản tin đã phát — đọc GET /api/v1/statistics/stations. Xuất Excel dạng CSV phía
 * trình duyệt. Phần "Phân tích chi tiết" (bốn nhóm thống kê) đã bỏ theo yêu cầu khách.
 * Trang "Thống kê thiết bị theo địa bàn" của bảng điện tử dùng lại bảng này (`loaiThietBi`).
 */
import { useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { thietBiApi, type DanhSachDaiTruyenThanh } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { oCsv } from './MH06NhapKhau';
import './lich-nhat-ky.css';

export function MH33ThongKe(): ReactNode {
  return (
    <div className="man-hinh man-hinh--thong-ke" data-man-hinh="MH-33">
      <header className="man-hinh__dau">
        <h1>{viLichNhatKy.thongKe.tieuDeLoa}</h1>
      </header>
      <BangDaiTruyenThanh />
    </div>
  );
}

/** Dựng CSV (có BOM để Excel đọc đúng tiếng Việt) theo đúng thứ tự cột trên màn hình. */
export function csvDaiTruyenThanh(ds: DanhSachDaiTruyenThanh): string {
  const tk = viLichNhatKy.thongKe;
  const hang = [
    [
      tk.cot.donVi,
      tk.cot.daiCntt,
      tk.cot.tongThietBi,
      tk.cot.dangKetNoi,
      tk.cot.matKetNoi,
      tk.cot.tongBanTin,
    ],
    [
      ds.tinh.ten,
      `${ds.tinh.soXaCntt}/${ds.tinh.soXa}`,
      ds.tinh.tongThietBi,
      ds.tinh.dangKetNoi,
      ds.tinh.matKetNoi,
      ds.tinh.tongBanTinDaPhat,
    ],
    ...ds.items.map((d) => [
      d.ten,
      d.ungDungCntt ? tk.coCntt : tk.chuaCntt,
      d.tongThietBi,
      d.dangKetNoi,
      d.matKetNoi,
      d.tongBanTinDaPhat,
    ]),
  ];
  return String.fromCharCode(0xfeff) + hang.map((h) => h.map(oCsv).join(',')).join('\r\n');
}

function taiTepCsv(noiDung: string, tenTep: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([noiDung], { type: 'text/csv;charset=utf-8' }));
  a.download = tenTep;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Bảng "Danh sách đài truyền thanh cấp xã" theo mẫu: dòng tỉnh (thu gọn/mở rộng được)
 * và mỗi xã, phường một dòng; cột "Đài ứng dụng CNTT" là xã có thiết bị đã kết nối nguồn tỉnh.
 * `loaiThietBi` = bảng điện tử: cùng cách tính, số liệu của bảng tin (DV016, nhật ký DV013).
 */
export function BangDaiTruyenThanh({
  loaiThietBi = 'cum_thu_phat_thanh',
  nhan = viLichNhatKy.thongKe.tieuDeLoa,
  tenTep = viLichNhatKy.thongKe.tenTepDai,
  api = thietBiApi,
}: {
  loaiThietBi?: 'cum_thu_phat_thanh' | 'bang_tin_dien_tu';
  nhan?: string;
  tenTep?: string;
  api?: Pick<typeof thietBiApi, 'danhSachDai'>;
} = {}): ReactNode {
  const tk = viLichNhatKy.thongKe;
  const [mo, datMo] = useState(true);
  const kq = dungTaiDuLieu<DanhSachDaiTruyenThanh>({
    tai: (signal) =>
      loaiThietBi === 'bang_tin_dien_tu' ? api.danhSachDai(signal, loaiThietBi) : api.danhSachDai(signal),
    laRong: (d) => d.items.length === 0 && d.tinh.tongThietBi === 0,
    dangLoc: false,
    phuThuoc: [loaiThietBi],
  });
  const ds = kq.duLieu;
  const so = (v: number): string => v.toLocaleString('vi-VN');
  return (
    <section data-vung="bang-dai-truyen-thanh">
      <div className="lnk-loc lnk-loc--phai">
        <button
          type="button"
          className="nut lnk-nut-excel"
          disabled={!ds}
          onClick={() =>
            ds &&
            taiTepCsv(csvDaiTruyenThanh(ds), dienVao(tenTep, { ngay: new Date().toISOString().slice(0, 10) }))
          }
        >
          {viLichNhatKy.chung.xuatExcel}
        </button>
      </div>
      {kq.trangThai === 'dang-tai' ? <KhungXuong soCot={6} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {ds ? (
        <div className="bang-cuon">
          <table className="bang lnk-bang dai-bang" aria-label={nhan}>
            <thead>
              <tr>
                <th scope="col">{tk.cot.donVi}</th>
                <th scope="col">{tk.cot.daiCntt}</th>
                <th scope="col">{tk.cot.tongThietBi}</th>
                <th scope="col">{tk.cot.dangKetNoi}</th>
                <th scope="col">{tk.cot.matKetNoi}</th>
                <th scope="col">{tk.cot.tongBanTin}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="dai-bang__tinh" data-khoa-dong={ds.tinh.nguonId}>
                <td>
                  <button
                    type="button"
                    className="dai-bang__mo"
                    aria-expanded={mo}
                    aria-label={mo ? tk.thuGon : tk.moRong}
                    onClick={() => datMo(!mo)}
                  >
                    {mo ? '−' : '+'}
                  </button>
                  {ds.tinh.ten}
                </td>
                <td>
                  <span
                    className="dai-the dai-the--tong"
                    title={dienVao(tk.tyLeCntt, { co: ds.tinh.soXaCntt, tong: ds.tinh.soXa })}
                  >
                    {ds.tinh.soXaCntt}/{ds.tinh.soXa}
                  </span>
                </td>
                <td>{so(ds.tinh.tongThietBi)}</td>
                <td>{so(ds.tinh.dangKetNoi)}</td>
                <td>{so(ds.tinh.matKetNoi)}</td>
                <td>{so(ds.tinh.tongBanTinDaPhat)}</td>
              </tr>
              {mo
                ? ds.items.map((d) => (
                    <tr key={d.nguonId} className="dai-bang__xa" data-khoa-dong={d.nguonId}>
                      <td className="dai-bang__ten-xa">{d.ten}</td>
                      <td>
                        <span className={`dai-the ${d.ungDungCntt ? 'dai-the--co' : 'dai-the--chua'}`}>
                          {d.ungDungCntt ? tk.coCntt : tk.chuaCntt}
                        </span>
                      </td>
                      <td>{so(d.tongThietBi)}</td>
                      <td>{so(d.dangKetNoi)}</td>
                      <td>{so(d.matKetNoi)}</td>
                      <td>{so(d.tongBanTinDaPhat)}</td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      ) : null}
      {ds && ds.items.length === 0 ? (
        <div className="bang__rong">
          <TrangThaiRong moTa={tk.rongDai} />
        </div>
      ) : null}
    </section>
  );
}
